// Business time zone. Functions run in UTC, so "today", "overdue" and the
// Todoist due date must be computed here, never with the server clock's zone.
export const HUB_TZ = 'Europe/Vilnius';

interface WallParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

const WALL = new Intl.DateTimeFormat('en-GB', {
  timeZone: HUB_TZ,
  hourCycle: 'h23',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

function wallParts(instant: Date): WallParts {
  const parts = WALL.formatToParts(instant);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour'),
    minute: get('minute'),
    second: get('second'),
  };
}

const pad = (n: number) => String(n).padStart(2, '0');

/** "YYYY-MM-DD" of an instant in Vilnius. */
export function hubDate(instant: Date = new Date()): string {
  const p = wallParts(instant);
  return `${p.year}-${pad(p.month)}-${pad(p.day)}`;
}

/** Hour 0–23 of an instant in Vilnius (job guards). */
export function hubHour(instant: Date = new Date()): number {
  return wallParts(instant).hour;
}

/** Vilnius offset from UTC in minutes at an instant (+180 summer, +120 winter). */
export function hubOffsetMinutes(instant: Date): number {
  const p = wallParts(instant);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return Math.round((asUtc - Math.floor(instant.getTime() / 1000) * 1000) / 60000);
}

/**
 * A wall-clock time written in Vilnius without an offset
 * ("2026-09-28 18:33", "2026-09-28T18:33:00") → UTC ISO string.
 */
export function vilniusWallToUtc(wall: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/.exec(wall.trim());
  if (!m) throw new Error(`Not a wall-clock time: ${wall}`);
  const [, y, mo, d, h, mi, s] = m;
  const guess = Date.UTC(+y!, +mo! - 1, +d!, +h!, +mi!, +(s ?? 0));
  // Two passes settle the offset across a DST change.
  let utc = guess - hubOffsetMinutes(new Date(guess)) * 60000;
  utc = guess - hubOffsetMinutes(new Date(utc)) * 60000;
  return new Date(utc).toISOString();
}

/** A wall-clock time written in GMT without an offset → UTC ISO string. */
export function gmtWallToUtc(wall: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/.exec(wall.trim());
  if (!m) throw new Error(`Not a wall-clock time: ${wall}`);
  const [, y, mo, d, h, mi, s] = m;
  return new Date(Date.UTC(+y!, +mo! - 1, +d!, +h!, +mi!, +(s ?? 0))).toISOString();
}

/**
 * A real calendar date "YYYY-MM-DD". JavaScript would quietly turn 2026-02-30
 * into 2 March, so the parts are checked to survive the round trip.
 */
export function isCalendarDate(v: string): boolean {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  if (!m) return false;
  const d = new Date(Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!));
  return d.getUTCFullYear() === +m[1]! && d.getUTCMonth() === +m[2]! - 1 && d.getUTCDate() === +m[3]!;
}

/**
 * A deadline or target date a plan may hold: a calendar date in 2000–2100. Not "0202-05-15", which a
 * date field holds while its year is typed; on the Gantt such a date would stretch the time axis.
 */
export const isPlanDate = (v: string): boolean => isCalendarDate(v) && v >= '2000-01-01' && v <= '2100-12-31';

/** Whole days from `today` (yyyy-mm-dd, Vilnius) to the date `d`; negative = past; null when either is not a date. */
export function daysUntil(d: string, today: string = hubDate()): number | null {
  const e = Date.parse(`${d}T00:00:00Z`);
  const t = Date.parse(`${today}T00:00:00Z`);
  if (!d || Number.isNaN(e) || Number.isNaN(t)) return null;
  return Math.round((e - t) / 86_400_000);
}

/** Due date strictly before today in Vilnius (due today is not overdue). */
export function isOverdue(due: string | null | undefined, now: Date = new Date()): boolean {
  return !!due && due < hubDate(now);
}

/** "YYYY-MM-DD HH:mm" in Vilnius, for an ISO instant (as the old sheet showed times). '' if not a date. */
export function hubStamp(iso: string): string {
  const d = new Date(iso);
  if (!iso || Number.isNaN(d.getTime())) return '';
  const p = wallParts(d);
  return `${p.year}-${pad(p.month)}-${pad(p.day)} ${pad(p.hour)}:${pad(p.minute)}`;
}
